import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { login, forgotPassword } from '../../actions/authActions';
import { clearErrors } from '../../actions/errorActions';
import { X, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import ReactDOM from 'react-dom';

class LoginModal extends Component {
  state = {
    modal: false,
    email: '',
    password: '',
    showPassword: false,
    msg: null,
    showForgotPassword: false,
    forgotPasswordEmail: '',
    forgotPasswordMessage: null
  };

  static propTypes = {
    isAuthenticated: PropTypes.bool,
    error: PropTypes.object.isRequired,
    forgotPasswordMessage: PropTypes.string,
    login: PropTypes.func.isRequired,
    forgotPassword: PropTypes.func.isRequired,
    clearErrors: PropTypes.func.isRequired
  };

  componentDidUpdate(prevProps) {
    const { error, isAuthenticated, forgotPasswordMessage } = this.props;
    
    if (error !== prevProps.error) {
      if (error.id === 'LOGIN_FAIL') {
        // Handle different error message structures safely
        let errorMessage = 'Login failed. Please try again.';
        
        if (error.msg) {
          if (typeof error.msg === 'string') {
            errorMessage = error.msg;
          } else if (error.msg.msg) {
            errorMessage = error.msg.msg;
          } else if (error.msg.message) {
            errorMessage = error.msg.message;
          }
        }
        
        this.setState({ msg: errorMessage });
      } else {
        this.setState({ msg: null });
      }
    }

    // Handle forgot password message
    if (forgotPasswordMessage !== prevProps.forgotPasswordMessage) {
      this.setState({ forgotPasswordMessage });
    }

    if (this.state.modal) {
      if (isAuthenticated) {
        this.toggle();
      }
    }
  }

  toggle = () => {
    this.props.clearErrors();
    this.setState({
      modal: !this.state.modal
    });
  };

  onChange = e => {
    this.setState({ [e.target.name]: e.target.value });
  };

  onForgotPasswordChange = e => {
    this.setState({ forgotPasswordEmail: e.target.value });
  };

  togglePasswordVisibility = () => {
    this.setState({ showPassword: !this.state.showPassword });
  };

  showForgotPasswordForm = () => {
    this.setState({ 
      showForgotPassword: true, 
      msg: null, 
      forgotPasswordMessage: null 
    });
  };

  hideForgotPasswordForm = () => {
    this.setState({ 
      showForgotPassword: false, 
      forgotPasswordEmail: '', 
      forgotPasswordMessage: null 
    });
  };

  onSubmit = e => {
    e.preventDefault();
    const { email, password } = this.state;
    const user = { email, password };
    this.props.login(user);
  };

  onForgotPasswordSubmit = e => {
    e.preventDefault();
    const { forgotPasswordEmail } = this.state;
    if (forgotPasswordEmail.trim()) {
      this.props.forgotPassword({ email: forgotPasswordEmail });
    }
  };

  render() {
    const { showForgotPassword, forgotPasswordMessage } = this.state;
    
    const modalContent = this.state.modal ? (
      <div className="fixed inset-0 z-[9999] overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
        <div className="relative bg-white rounded-xl md:max-w-md w-full sm:mx-4 mx-0 p-6 shadow-lg border border-gray-200">
          {!showForgotPassword ? (
            // Login Form
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Login</h3>
                <button 
                  onClick={this.toggle} 
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors duration-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {this.state.msg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg">
                  {this.state.msg}
                </div>
              )}

              <form onSubmit={this.onSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
                    onChange={this.onChange}
                    value={this.state.email}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={this.state.showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      placeholder="Enter your password"
                      className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
                      onChange={this.onChange}
                      value={this.state.password}
                      style={{
                        WebkitTextSecurity: this.state.showPassword ? 'none' : 'disc',
                        msReveal: 'none',
                        msRevealPassword: 'none'
                      }}
                      data-ms-reveal="false"
                    />
                    <button
                      type="button"
                      onClick={this.togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200"
                      aria-label={this.state.showPassword ? "Hide password" : "Show password"}
                    >
                      {this.state.showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-[#1086a4] hover:bg-[#0c6d85] text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                >
                  Login
                </button>
              </form>

              {/* Forgot Password Link */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={this.showForgotPasswordForm}
                  className="text-[#1086a4] hover:text-[#0c6d85] text-sm font-medium transition-colors duration-200"
                >
                  Forgot Password?
                </button>
              </div>
            </>
          ) : (
            // Forgot Password Form
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <button
                    onClick={this.hideForgotPasswordForm}
                    className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors duration-200 mr-3"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <h3 className="text-2xl font-bold text-gray-900">Reset Password</h3>
                </div>
                <button 
                  onClick={this.toggle} 
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors duration-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {forgotPasswordMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-600 rounded-lg">
                  {forgotPasswordMessage}
                </div>
              )}

              <div className="mb-4 text-sm text-gray-600">
                Enter your email address and we'll send you a new password.
              </div>

              <form onSubmit={this.onForgotPasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="forgotPasswordEmail"
                    id="forgotPasswordEmail"
                    placeholder="Enter your email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
                    onChange={this.onForgotPasswordChange}
                    value={this.state.forgotPasswordEmail}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-[#1086a4] hover:bg-[#0c6d85] text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                >
                  Send New Password
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={this.hideForgotPasswordForm}
                  className="text-[#1086a4] hover:text-[#0c6d85] text-sm font-medium transition-colors duration-200"
                >
                  Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    ) : null;

    return (
      <>
        <button onClick={this.toggle} className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200">
          Login
        </button>
        {modalContent && ReactDOM.createPortal(
          modalContent,
          document.body
        )}
      </>
    );
  }
}

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  error: state.error,
  forgotPasswordMessage: state.auth.forgotPasswordMessage
});

export default connect(
  mapStateToProps,
  { login, forgotPassword, clearErrors }
)(LoginModal);
